import { useEffect, useRef } from "react";

import { useGetBroadcastId, useSetComments, useSetPinnedComment }                                         from "_common/hooks/stream";
import { useSetDynamicData, useSetTotalReactions, useSetReactions }                                       from "_common/hooks/stream";
import { useSetUpdateSession }                                                                            from "_jotai/liveSession";
import { getBroadcastComments, getChatTips, getPinnedComments, getReactionsListener, getSessionListener } from "_services/firebase/broadcast";

const useFirestore = connect => {
	const broadcastId       = useGetBroadcastId ();
	const setCommentsState  = useSetComments ();
	const setPinnedComment  = useSetPinnedComment ();
	const setDynamicData    = useSetDynamicData ();
	const setReactions      = useSetReactions ();
	const setTotalReactions = useSetTotalReactions ();
	const updateSession     = useSetUpdateSession ();

	let __listener         = useRef ( null );
	let __pinnedListener   = useRef ( null );
	let __sessionListener  = useRef ( null );
	let __rxnListener      = useRef ( null );
	let __chatTipsListener = useRef ( null );

	useEffect ( () => {
		if ( !broadcastId || !connect )
			return;

		intiateListeners ( broadcastId );

		return cleanupListeners;
	}, [ broadcastId, connect ] );

	const intiateListeners = broadcastId => {
		invokeListeners ( broadcastId );
		pinnedCommentListener ( broadcastId );
		sessionListener ( broadcastId );
		reactionsListListener ( broadcastId );
		chatTipsListener ( broadcastId );
	};

	const cleanupListeners = () => {
		__listener.current && __listener.current ();
		__pinnedListener.current && __pinnedListener.current ();
		__sessionListener.current && __sessionListener.current ();
		__rxnListener.current && __rxnListener.current ();
		__chatTipsListener.current && __chatTipsListener.current ();
	};

	const updateComments = comment => {
		if ( !comment )
			return;

		setCommentsState ( prevState => [ ...prevState, comment ].slice ( -250 ) );
	};

	const invokeListeners = async broadcastId => {
		__listener.current = await getBroadcastComments ( broadcastId, async ( comments, error ) => {

			/* if ( error && error.code === "permission-denied" ) {
				currentRetries ++;

				if ( currentRetries > totalRetries )
					return;

				await invokeListeners ();

				return;
			}*/

			comments.docChanges ().forEach ( ele => {
				const data      = ele.doc.data ();
				const isDeleted = data.deleted;

				if ( ele.type === "added" && data && !isDeleted ) {
					updateComments ( data );
				}

				if ( isDeleted ) {
					setCommentsState ( prevState => prevState.filter ( item => item.id !== data.id ) );
				}

			} );

		} );

		return __listener.current;
	};

	const pinnedCommentListener = async broadcastId => {
		__pinnedListener.current = await getSessionListener ( broadcastId, doc => {
			doc.docChanges ().forEach ( async ele => {
				const data = ele.doc && ele.doc.data ();

				if ( !data )
					return;

				if ( data.pinnedCommentId ) {
					const comment = await getPinnedComments ( broadcastId, String ( data.pinnedCommentId ) );

					setPinnedComment ( comment );
				} else {
					setPinnedComment ( null );
				}
			} );
		} );

		return __pinnedListener.current;
	};

	const sessionListener = async () => {
		__sessionListener.current = await getSessionListener ( broadcastId, doc => {
			doc.docChanges ().forEach ( ele => {
				const data = ele.doc && ele.doc.data ();

				setDynamicData ( data );
				updateSession ( prevState => ( { ...prevState, ...data } ) );
			} );
		} );

		return __sessionListener.current;
	};

	const reactionsListListener = async broadcastId => {
		__rxnListener.current = await getReactionsListener ( broadcastId, doc => {
			doc.docChanges ().forEach ( ele => {
				const data = ele.doc && ele.doc.data ();

				if ( !data )
					return;

				setTotalReactions ( data?.totalReactions );
				setReactions ( {
					[data?.id]: {
						previousCount : data?.reactionCount || 0,
						showCount     : data?.reactionCount || 0
					}
				} );
			} );
		} );

		return __rxnListener.current;
	};

	const chatTipsListener = async broadcastId => {
		__chatTipsListener.current = await getChatTips ( broadcastId, doc => {
			doc.docChanges ().forEach ( ele => {
				const data = ele.doc && ele.doc.data ();

				if ( !data || data?.chatTip?.ctaType === "AFFL_AD" || data?.chatTip?.ctaType === "SQUAD_PLAY" )
					return;

				if ( !data?.chatTip )
					return;

				updateComments ( data );
			} );
		} );

		return __chatTipsListener.current;
	};
};

export default useFirestore;